import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Cpu, Server, Database, TestTube, Container, GitBranch } from "lucide-react";

const stackLayers = [
  {
    icon: <Server className="h-5 w-5 text-tasktide-teal" />,
    title: 'FastAPI Backend',
    desc: 'Typed Python APIs with automatic OpenAPI docs, async route handlers, and dependency injection. Every endpoint is documented and tested.'
  },
  {
    icon: <Database className="h-5 w-5 text-tasktide-purple" />,
    title: 'SQLModel ORM',
    desc: 'Single-model approach using Pydantic + SQLAlchemy. Type-safe database operations with migration support via Alembic.'
  },
  {
    icon: <Cpu className="h-5 w-5 text-tasktide-orange" />,
    title: 'Local LLM Integration',
    desc: 'Runs on Ollama, llama.cpp, or any OpenAI-compatible local endpoint. No cloud dependencies. No API bills. Your data stays on your machine.'
  },
  {
    icon: <TestTube className="h-5 w-5 text-emerald-400" />,
    title: 'pytest + Coverage',
    desc: 'Every product ships with a test suite: unit tests for logic, integration tests for routes, and end-to-end tests for critical paths. Target: 80%+ coverage.'
  },
  {
    icon: <Container className="h-5 w-5 text-blue-400" />,
    title: 'Docker + Docker Compose',
    desc: 'One command to run the entire stack. Dockerfile for the app, compose file for Postgres/Redis if needed. Works on any machine with Docker installed.'
  },
  {
    icon: <GitBranch className="h-5 w-5 text-rose-400" />,
    title: 'GitHub Actions CI',
    desc: 'Every commit triggers linting, type checking, test runs, and build verification. Releases are auto-tagged with semantic versioning.'
  }
];

const AboutTechnology = () => {
  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          <div className="mb-4">
            <Link to="/about" className="text-sm text-gray-500 hover:text-tasktide-teal transition-colors">
              ← Back to About
            </Link>
          </div>

          <div className="mb-16">
            <span className="text-sm text-tasktide-teal font-medium">Our Technology</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              The Stack Behind the Pipeline
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl">
              We do not chase trends. The entire pipeline runs on a single, proven stack 
              chosen for speed, reliability, and local deployment. Every product inherits 
              this foundation. No surprises. No dependency hell.
            </p>
          </div>

          <div className="space-y-4 mb-16">
            {stackLayers.map((layer) => (
              <div key={layer.title} className="flex items-start gap-4 glass-card p-5 rounded-lg border border-white/5">
                <div className="w-10 h-10 rounded-lg bg-dark-300 flex items-center justify-center flex-shrink-0">
                  {layer.icon}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{layer.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{layer.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="glass-card p-8 rounded-xl border border-white/5 mb-16">
            <h2 className="text-xl font-bold text-white mb-4">The 1-Hour Pipeline</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              From product brief to committed code takes roughly 60 minutes. Here is how that breaks down:
            </p>
            <ul className="space-y-3">
              {[
                '00:00 — Agent reads the brief and plans the schema, routes, and UI screens.',
                '00:10 — Backend scaffold: FastAPI app, SQLModel models, Alembic migration.',
                '00:25 — Route handlers: CRUD operations, auth if needed, input validation.',
                '00:35 — Test suite: fixtures, in-memory DB, route coverage, edge cases.',
                '00:45 — React UI: screens, forms, tables, dark theme, responsive layout.',
                '00:55 — README, env.example, docker-compose, final lint and type check.',
                '01:00 — Commit to private GitHub repo, tag release, queue next brief.'
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                  <span className="text-tasktide-teal font-mono flex-shrink-0 w-16">{step.split(' — ')[0]}</span>
                  <span>{step.split(' — ')[1]}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button asChild className="bg-gradient-to-r from-tasktide-teal to-tasktide-blue text-white">
              <Link to="/projects">
                Browse Products <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50">
              <Link to="/contact">Get in Touch →</Link>
            </Button>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutTechnology;